<template>
  <div class="w-full flex flex-col">
    <div class="mb-2.5">
      <p class="text-sm text-[#58595b] mb-2.5 text-center font-medium">Standard Flow Rate (SCFM) Calculation:</p>
      <ul class="text-sm mb-2.5 flex flex-col gap-1">
        <li>CFM = actual cubic feet per minute</li>
        <li>PSI = actual pounds per square inch at Gauge</li>
        <li>T = actual temperature in °F</li>
        <li>SCFM = CFM * (PSI + 14.7 / 14.7) * (528 / (T + 460))</li>
      </ul>
      <div class="font-bold text-center">Fill in 3 values to determine the SCFM</div>
    </div>
    <div class="w-full flex flex-col gap-1">
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">CFM</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.cfm}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.cfm}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.cfm}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.cfm) }"
            v-model="state.cfm" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">PSI</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.psi}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.psi}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.psi}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.psi) }"
            v-model="state.psi" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">°F</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.temp}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.temp}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.temp}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.temp) }"
            v-model="state.temp" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">SCFM</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.scfm}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.scfm}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.scfm}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.scfm) }"
            v-model="state.scfm" />
        </label>
      </div>
      <div class="w-full flex items-center justify-center px-4 gap-2.5 mt-1">
        <div
          class="text-sm py-1.5 px-3 rounded text-white bg-red hover:bg-dark-red cursor-pointer flex items-center justify-center transition-colors duration-300"
          :class="{ 'grayscale pointer-events-none': !(state.cfm !== '' || state.psi !== '' || state.temp !== '') }"
          @click="resetAction">
          Reset
        </div>
        <div
          class="w-full max-w-44 text-sm py-1.5 px-3 rounded text-white bg-red hover:bg-dark-red cursor-pointer flex items-center justify-center transition-colors duration-300"
          :class="{ 'grayscale pointer-events-none': !state.isFormValid }"
          @click="calculateAction">
          Calculate
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, watch } from 'vue';
import GlobalHelper from '@/helpers/global/global.helper';

enum CalculatorFieldsTypes {
  cfm = 'cfm',
  psi = 'psi',
  temp = 'temp',
  scfm = 'scfm',
}

const calcType: string = 'standard-flowrate-calculator';

const state = reactive({
  cfm: '' as string | number,
  psi: '' as string | number,
  temp: '' as string | number,
  scfm: '' as string | number,
  isFormValid: false as boolean,
  inputFields: [CalculatorFieldsTypes.cfm, CalculatorFieldsTypes.psi, CalculatorFieldsTypes.temp] as string[],
});

const checkIsFormValid = () => {
  state.isFormValid = state.cfm !== '' && state.psi !== '' && state.temp !== '';
};

const resetAction = () => {
  state.cfm = '';
  state.psi = '';
  state.temp = '';
  state.scfm = '';
};

const calculateAction = () => {
  if (state.isFormValid) {
    state.scfm = GlobalHelper.roundToDecimals(Number(state.cfm) * ((Number(state.psi) + 14.7) / 14.7) * (528 / (Number(state.temp) + 460)), 4);
  }
};

watch(
  () => state.cfm,
  () => {
    checkIsFormValid();
  }
);

watch(
  () => state.psi,
  () => {
    checkIsFormValid();
  }
);

watch(
  () => state.temp,
  () => {
    checkIsFormValid();
  }
);
</script>
